import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ScrollText, Shield, AlertTriangle, UserCheck, RefreshCw, Clock } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';

const ACTION_ICONS = {
  expense_approved: { icon: '✓', color: 'bg-green-500/20 text-green-600' },
  expense_rejected: { icon: '✗', color: 'bg-red-500/20 text-red-600' },
  expense_reversed: { icon: '↩', color: 'bg-gray-500/20 text-gray-500' },
  fine_issued: { icon: '₹', color: 'bg-yellow-500/20 text-yellow-600' },
  user_approved: { icon: '✓', color: 'bg-green-500/20 text-green-600' },
  user_rejected: { icon: '✗', color: 'bg-red-500/20 text-red-600' },
  user_archived: { icon: '⊘', color: 'bg-gray-500/20 text-gray-500' },
  group_admin_promoted: { icon: '↑', color: 'bg-blue-500/20 text-blue-600' },
  group_admin_demoted: { icon: '↓', color: 'bg-orange-500/20 text-orange-600' },
};

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/audit/logs?limit=100')
      .then(res => setLogs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="audit-forbidden">
        <ScrollText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Admin Only</h2>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-4" data-testid="audit-log-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <ScrollText className="w-6 h-6 text-primary" />
          Audit Log
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Who did what, when — full accountability trail</p>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-8">Loading...</p>
      ) : logs.length === 0 ? (
        <Card className="p-8 text-center border-2 rounded-2xl">
          <ScrollText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No actions logged yet</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => {
            const actionStyle = ACTION_ICONS[log.action] || { icon: '•', color: 'bg-primary/20 text-primary' };
            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <Card className="p-3 border rounded-xl" data-testid={`audit-log-${log.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${actionStyle.color}`}>
                      {actionStyle.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-semibold">{log.actor_name}</span>
                        {' '}
                        <span className="text-muted-foreground">{log.details}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
